import React from 'react';
import { View, Text, TextInput, Switch, StyleSheet, Platform } from 'react-native';
import { useNavigate } from 'react-router-dom';

interface AccountCreationStepProps {
  fullName: string;
  email: string;
  password: string;
  phoneNumber: string;
  acceptedTerms: boolean;
  onFullNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
  onAcceptedTermsChange: (value: boolean) => void;
}

export const AccountCreationStep = ({
  fullName,
  email,
  password,
  phoneNumber,
  acceptedTerms,
  onFullNameChange,
  onEmailChange,
  onPasswordChange,
  onPhoneNumberChange,
  onAcceptedTermsChange
}: AccountCreationStepProps) => {
  const navigate = useNavigate();

  return (
    <View style={styles.stepContainer}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepTitle}>Create Your Account</Text>
        <Text style={styles.stepSubtitle}>
          Set up your account to get started with HoneyDone
        </Text>
      </View>
      <View style={styles.formFields}>
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Full Name</Text>
          <TextInput
            style={styles.input}
            value={fullName}
            onChangeText={onFullNameChange}
            placeholder="Enter your full name"
          />
        </View>
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Phone Number</Text>
          <TextInput
            style={styles.input}
            value={phoneNumber}
            onChangeText={onPhoneNumberChange}
            placeholder="Enter your phone number"
            keyboardType="phone-pad"
          />
        </View>
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Email</Text>
          <TextInput
            style={styles.input}
            value={email}
            onChangeText={onEmailChange}
            placeholder="Enter your email"
            keyboardType="email-address"
            autoCapitalize="none"
          />
        </View>
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Password</Text>
          <TextInput
            style={styles.input}
            value={password}
            onChangeText={onPasswordChange}
            placeholder="Create a password"
            secureTextEntry
            autoCapitalize="none"
          />
        </View>
        <View style={styles.termsContainer}>
          <View style={styles.termsRow}>
            <Switch
              value={acceptedTerms}
              onValueChange={onAcceptedTermsChange}
              trackColor={{ false: '#d1d5db', true: '#000000' }}
              thumbColor={acceptedTerms ? '#ffffff' : '#f3f4f6'}
            />
            <View style={styles.termsTextContainer}>
              <Text style={styles.termsText}>
                I accept the{' '}
                <Text
                  style={styles.termsLink}
                  onPress={() => {
                    if (Platform.OS === 'web') {
                      window.open('/terms-of-service', '_blank');
                    } else {
                      navigate('/terms-of-service');
                    }
                  }}
                >
                  Terms of Service
                </Text>
                {' '}and{' '}
                <Text
                  style={styles.termsLink}
                  onPress={() => {
                    if (Platform.OS === 'web') {
                      window.open('/privacy-policy', '_blank');
                    } else {
                      navigate('/privacy-policy');
                    }
                  }}
                >
                  Privacy Policy
                </Text>
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  stepContainer: {
    gap: 32,
  },
  stepHeader: {
    gap: 8,
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  stepSubtitle: {
    fontSize: 16,
    color: '#4b5563',
  },
  formFields: {
    gap: 16,
  },
  inputGroup: {
    gap: 4,
    ...(Platform.OS === 'web' && {
      flex: 1,
      minWidth: 200,
    }),
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 4,
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    backgroundColor: '#ffffff',
    flex: 1,
  },
  termsContainer: {
    marginTop: 16,
  },
  termsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  termsTextContainer: {
    flex: 1,
  },
  termsText: {
    fontSize: 14,
    color: '#4b5563',
  },
  termsLink: {
    color: '#000000',
    ...(Platform.OS === 'web' && {
      textDecorationLine: 'underline',
    }),
  },
});
